// ============================================================================
// DaysSection — per-day editor for Multi-Day Events.
// ============================================================================
// Sits inside EventForm. By default shows a single Day 1 row with date + times
// auto-derived from the event start/end. Admin can "Add a day" to make it
// multi-day. Each row has an Advanced section (collapsed by default) that
// reveals per-day overrides: doors open, buffer minutes (before/after),
// provisions, agenda markdown, notes.
//
// Override semantics:
//   * Empty override → inherit from event-level field (rendered as placeholder).
//   * Provisions REPLACE event-level (not additive). Empty array means "none
//     this day" while NULL means "inherit". Form uses two states: a "use
//     event-level" toggle for opt-in override.
//
// Output: an array of EventDay objects (no day_id for new rows).
// ============================================================================

import { useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronRight, Coffee, Utensils, UtensilsCrossed, Cookie } from 'lucide-react';
import type { EventDay, Provision } from '../api';

interface Props {
  days: EventDay[];
  onChange: (days: EventDay[]) => void;
  disabled?: boolean;
  // Defaults shown as placeholder when a day's override field is null.
  fallbackStartTime: string;       // HH:MM from event start_date
  fallbackEndTime: string;         // HH:MM from event end_date
  fallbackDoorsOpen: string;
  fallbackProvisions: Provision[]; // event-level provisions
}

const PROVISION_OPTIONS: { value: Provision; label: string; Icon: any }[] = [
  { value: 'breakfast', label: 'Breakfast', Icon: Coffee },
  { value: 'lunch',     label: 'Lunch',     Icon: Utensils },
  { value: 'dinner',    label: 'Dinner',    Icon: UtensilsCrossed },
  { value: 'snacks',    label: 'Snacks',    Icon: Cookie },
];

function nextIndex(days: EventDay[]): number {
  return days.length === 0 ? 1 : Math.max(...days.map((d) => d.day_index)) + 1;
}

function addDayAfter(days: EventDay[], lastDate: string): EventDay {
  // Add a day immediately after the last day's date.
  const baseDate = lastDate ? new Date(lastDate + 'T00:00:00Z') : new Date();
  const next = new Date(baseDate.getTime() + 24 * 60 * 60 * 1000);
  return {
    day_index: nextIndex(days),
    day_date: next.toISOString().slice(0, 10),
  };
}

export function DaysSection({
  days, onChange, disabled,
  fallbackStartTime, fallbackEndTime, fallbackDoorsOpen, fallbackProvisions,
}: Props) {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const toggleExpanded = (idx: number) => {
    const next = new Set(expanded);
    if (next.has(idx)) next.delete(idx);
    else next.add(idx);
    setExpanded(next);
  };

  const updateDay = (idx: number, patch: Partial<EventDay>) => {
    onChange(days.map((d, i) => i === idx ? { ...d, ...patch } : d));
  };

  const removeDay = (idx: number) => {
    if (days.length <= 1) return;
    const next = days.filter((_, i) => i !== idx);
    // Re-number day_index sequentially
    onChange(next.map((d, i) => ({ ...d, day_index: i + 1 })));
  };

  const addDay = () => {
    const lastDate = days[days.length - 1]?.day_date ?? '';
    onChange([...days, addDayAfter(days, lastDate)]);
  };

  const toggleDayProvision = (idx: number, p: Provision) => {
    const day = days[idx];
    const current = new Set(day.provisions ?? []);
    if (current.has(p)) current.delete(p);
    else current.add(p);
    updateDay(idx, { provisions: Array.from(current) });
  };

  const useEventLevelProvisions = (idx: number) => {
    updateDay(idx, { provisions: null });
  };

  return (
    <div className="space-y-3">
      {days.map((day, i) => {
        const isOpen = expanded.has(i);
        const inheritsProvisions = day.provisions == null;
        return (
          <div key={i} className="border border-gray-200 rounded-md bg-white">
            <div className="flex items-center gap-2 p-3 border-b border-gray-100">
              <span className="inline-flex items-center justify-center rounded-full bg-uif-green/10 text-uif-green-dark text-xs font-bold w-6 h-6 flex-shrink-0">
                {day.day_index}
              </span>
              <input
                type="date"
                value={day.day_date}
                onChange={(e) => updateDay(i, { day_date: e.target.value })}
                disabled={disabled}
                className="rounded border border-gray-300 px-2 py-1 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
              <input
                type="time"
                value={day.start_time?.slice(0, 5) ?? ''}
                onChange={(e) => updateDay(i, { start_time: e.target.value || null })}
                disabled={disabled}
                placeholder={fallbackStartTime}
                title={`Inherits ${fallbackStartTime} when blank`}
                className="rounded border border-gray-300 px-2 py-1 text-sm w-28"
              />
              <span className="text-gray-400 text-sm">–</span>
              <input
                type="time"
                value={day.end_time?.slice(0, 5) ?? ''}
                onChange={(e) => updateDay(i, { end_time: e.target.value || null })}
                disabled={disabled}
                placeholder={fallbackEndTime}
                title={`Inherits ${fallbackEndTime} when blank`}
                className="rounded border border-gray-300 px-2 py-1 text-sm w-28"
              />
              <button
                type="button"
                onClick={() => toggleExpanded(i)}
                className="ml-auto inline-flex items-center gap-1 text-xs text-uif-green-dark hover:bg-uif-green/5 rounded px-2 py-1"
                title="Doors-open override, buffer minutes, per-day provisions"
              >
                {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                Advanced overrides
              </button>
              {days.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeDay(i)}
                  disabled={disabled}
                  className="inline-flex items-center gap-1 text-xs text-red-700 hover:bg-red-50 rounded px-2 py-1 disabled:opacity-50"
                >
                  <Trash2 className="h-3 w-3" />
                  Remove
                </button>
              )}
            </div>

            {/* Primary per-day content: agenda + notes. Always visible so
                multi-day events can have distinct Day 1 / Day 2 agendas
                without hunting for the Advanced toggle. */}
            <div className="p-3 space-y-3 border-b border-gray-100">
              <label className="block">
                <span className="block text-xs font-medium text-gray-700 mb-1">
                  Day {day.day_index} agenda (markdown)
                  {day.agenda_md && (
                    <span className="ml-2 text-[10px] uppercase tracking-wider font-semibold text-uif-green-dark">
                      custom set
                    </span>
                  )}
                </span>
                <textarea
                  rows={5}
                  value={day.agenda_md ?? ''}
                  onChange={(e) => updateDay(i, { agenda_md: e.target.value || null })}
                  disabled={disabled}
                  placeholder="Leave blank to inherit the event-level agenda. Fill in to give this day its own agenda block (shown as a tab on the event detail page)."
                  className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm font-mono"
                />
              </label>

              <label className="block">
                <span className="block text-xs font-medium text-gray-700 mb-1">Notes for this day</span>
                <input
                  type="text"
                  value={day.notes ?? ''}
                  onChange={(e) => updateDay(i, { notes: e.target.value || null })}
                  disabled={disabled}
                  placeholder="e.g. dress code, parking, lunch off-site"
                  className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                />
              </label>
            </div>

            {isOpen && (
              <div className="p-3 space-y-3 bg-gray-50">
                <label className="block">
                  <span className="block text-xs font-medium text-gray-600 mb-1">Doors open (override)</span>
                  <input
                    type="text"
                    value={day.doors_open_text ?? ''}
                    onChange={(e) => updateDay(i, { doors_open_text: e.target.value || null })}
                    disabled={disabled}
                    placeholder={fallbackDoorsOpen || 'Inherits from event'}
                    className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                  />
                </label>

                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="block text-xs font-medium text-gray-600 mb-1">Buffer before (min)</span>
                    <input
                      type="number" min={0} max={480}
                      value={day.buffer_before_minutes ?? ''}
                      onChange={(e) => updateDay(i, { buffer_before_minutes: e.target.value === '' ? null : Number(e.target.value) })}
                      disabled={disabled}
                      placeholder="0"
                      className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                    />
                    <p className="text-[10px] text-gray-500 mt-0.5">Extends room booking before doors open (sound check, setup).</p>
                  </label>
                  <label className="block">
                    <span className="block text-xs font-medium text-gray-600 mb-1">Buffer after (min)</span>
                    <input
                      type="number" min={0} max={480}
                      value={day.buffer_after_minutes ?? ''}
                      onChange={(e) => updateDay(i, { buffer_after_minutes: e.target.value === '' ? null : Number(e.target.value) })}
                      disabled={disabled}
                      placeholder="0"
                      className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                    />
                    <p className="text-[10px] text-gray-500 mt-0.5">Extends room booking after the event ends (cleanup, load-out).</p>
                  </label>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-600">Provisions (override)</span>
                    {!inheritsProvisions && (
                      <button type="button" onClick={() => useEventLevelProvisions(i)}
                        className="text-[10px] text-uif-green-dark hover:underline">
                        ↩ Inherit from event
                      </button>
                    )}
                  </div>
                  {inheritsProvisions ? (
                    <button type="button" onClick={() => updateDay(i, { provisions: [...fallbackProvisions] })}
                      disabled={disabled}
                      className="text-xs text-gray-600 italic hover:text-gray-900 hover:underline disabled:opacity-50">
                      Inheriting event-level: {fallbackProvisions.length === 0 ? 'none' : fallbackProvisions.join(', ')} — click to override
                    </button>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {PROVISION_OPTIONS.map((opt) => {
                        const sel = (day.provisions ?? []).includes(opt.value);
                        return (
                          <button key={opt.value} type="button" onClick={() => toggleDayProvision(i, opt.value)}
                            disabled={disabled}
                            className={
                              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs border transition-colors ' +
                              (sel
                                ? 'bg-uif-green text-white border-uif-green-dark'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50')
                            }>
                            <opt.Icon className="h-3 w-3" />
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}

      <button
        type="button"
        onClick={addDay}
        disabled={disabled}
        className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 w-full justify-center"
      >
        <Plus className="h-4 w-4" />
        Add a day
      </button>

      <p className="text-[11px] text-gray-500">
        Times left blank inherit the event-level Start/End. Buffer minutes extend the room booking
        without changing what attendees see. Provisions can be overridden per day (e.g. breakfast Day 1 only).
      </p>
    </div>
  );
}
